import { useMemo } from "react";
import type { ShapePatternGameProps, PatternShape, PatternColor } from "./types";
import { PATTERN_SHAPES, PATTERN_COLORS } from "./types";

/**
 * ShapePatternGame Component
 *
 * A delightful shape pattern matching game where children identify
 * which shape comes next in a sequence. Features clean, recognizable
 * shapes with optional color theming and satisfying animations.
 *
 * Supports patterns like:
 * - AB: circle, square, circle, square, ?
 * - ABB: triangle, star, star, triangle, star, star, ?
 * - ABC: circle, square, triangle, circle, square, triangle, ?
 *
 * @example
 * <ShapePatternGame
 *   pattern={['circle', 'square', 'circle', 'square', 'circle']}
 *   missingIndex={4}
 *   options={['circle', 'square', 'triangle']}
 *   correctAnswer="circle"
 *   shapeColor="blue"
 *   onAnswer={handleAnswer}
 *   selectedAnswer={selected}
 * />
 */
export function ShapePatternGame({
  pattern,
  missingIndex,
  options,
  correctAnswer,
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
  shapeColor,
  discipline = "patterns",
}: ShapePatternGameProps) {
  // Color config for shapes (defaults to a nice blue gradient)
  const colorClass = useMemo(() => {
    if (shapeColor && PATTERN_COLORS[shapeColor]) {
      return PATTERN_COLORS[shapeColor];
    }
    // Default rainbow gradient feel for shapes
    return null;
  }, [shapeColor]);

  // Get shape display with proper styling
  const getShapeDisplay = (shape: PatternShape, size: "sm" | "md" | "lg" = "md") => {
    const shapeConfig = PATTERN_SHAPES[shape];
    const sizeClasses = {
      sm: "text-2xl md:text-3xl",
      md: "text-3xl md:text-4xl lg:text-5xl",
      lg: "text-4xl md:text-5xl lg:text-6xl",
    };

    // Generate rainbow color based on shape type for variety
    const shapeColorMap: Record<PatternShape, string> = {
      circle: "text-prism-red",
      square: "text-prism-blue",
      triangle: "text-prism-yellow",
      star: "text-prism-orange",
      heart: "text-[#FF9EBC]",
      diamond: "text-prism-purple",
    };

    return (
      <span
        className={`
          ${sizeClasses[size]}
          ${colorClass ? "text-white" : shapeColorMap[shape]}
          drop-shadow-md
          select-none
        `}
      >
        {shapeConfig.emoji}
      </span>
    );
  };

  return (
    <div className="flex flex-col items-center gap-8 w-full">
      {/* Shape Pattern Arena */}
      <div
        className="
          relative w-full
          bg-gradient-to-b from-white via-[#FAFAFA] to-[#F5F5F5]
          rounded-3xl p-6 md:p-8 lg:p-10
          border-4 border-neutral-200
          shadow-[0_8px_40px_rgba(0,0,0,0.08),inset_0_2px_0_rgba(255,255,255,0.9)]
        "
      >
        {/* Decorative elements */}
        <div className="absolute top-4 left-5 text-xl opacity-60 animate-float">
          ✨
        </div>
        <div
          className="absolute top-5 right-6 text-2xl opacity-50 animate-sparkle"
          style={{ animationDelay: "0.2s" }}
        >
          🌟
        </div>
        <div
          className="absolute bottom-4 left-6 text-lg opacity-40 animate-sparkle"
          style={{ animationDelay: "0.5s" }}
        >
          💫
        </div>
        <div
          className="absolute bottom-5 right-5 text-xl opacity-50 animate-float"
          style={{ animationDelay: "0.8s" }}
        >
          ⭐
        </div>

        {/* Pattern Display */}
        <div className="flex flex-wrap justify-center items-center gap-4 md:gap-5 lg:gap-6">
          {pattern.map((shape, index) => {
            const isMissing = index === missingIndex;

            return (
              <div
                key={`shape-${index}`}
                className="relative"
                style={{
                  animation: `star-pop 0.4s ease-out ${index * 100}ms both`,
                }}
              >
                {isMissing ? (
                  // Missing slot
                  <div
                    className={`
                      w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24
                      flex items-center justify-center
                      rounded-2xl
                      border-4 border-dashed
                      transition-all duration-300 ease-bounce
                      ${
                        selectedAnswer && isSubmitted && isCorrect
                          ? "border-meadow-500 bg-meadow-100/60 scale-105"
                          : selectedAnswer
                            ? "border-prism-purple bg-prism-purple/10 scale-102"
                            : "border-neutral-300/80 bg-white/80"
                      }
                    `}
                  >
                    {selectedAnswer ? (
                      <div
                        className={`
                          flex items-center justify-center
                          ${isSubmitted && isCorrect ? "animate-celebrate" : "animate-star-pop"}
                        `}
                      >
                        {getShapeDisplay(selectedAnswer, "md")}
                      </div>
                    ) : (
                      <span className="text-3xl md:text-4xl text-neutral-400/80 animate-pulse font-display">
                        ?
                      </span>
                    )}

                    {/* Celebration sparkles */}
                    {isSubmitted && isCorrect && (
                      <>
                        <span
                          className="absolute -top-3 -left-3 text-xl animate-sparkle"
                          style={{ animationDelay: "0s" }}
                        >
                          ✨
                        </span>
                        <span
                          className="absolute -top-3 -right-3 text-xl animate-sparkle"
                          style={{ animationDelay: "0.15s" }}
                        >
                          🌟
                        </span>
                        <span
                          className="absolute -bottom-3 left-0 text-lg animate-sparkle"
                          style={{ animationDelay: "0.3s" }}
                        >
                          ⭐
                        </span>
                        <span
                          className="absolute -bottom-3 -right-3 text-xl animate-sparkle"
                          style={{ animationDelay: "0.45s" }}
                        >
                          ✨
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  // Filled shape card
                  <div
                    className="
                      w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24
                      flex items-center justify-center
                      rounded-2xl
                      bg-white
                      border-4 border-neutral-200
                      shadow-[0_6px_0_0_rgba(0,0,0,0.08),0_8px_24px_rgba(0,0,0,0.08)]
                      transition-transform duration-200
                      hover:scale-102
                    "
                    aria-label={`${PATTERN_SHAPES[shape].label} shape`}
                  >
                    {getShapeDisplay(shape, "md")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Answer Options */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-xl text-neutral-800 mb-5">
          Which shape comes next?
        </h3>

        <div className="flex flex-wrap justify-center gap-4 md:gap-5">
          {options.map((shape, index) => {
            const isSelected = selectedAnswer === shape;
            const isCorrectOption = shape === correctAnswer;

            return (
              <button
                key={`option-${shape}-${index}`}
                type="button"
                onClick={() => !isSubmitted && onAnswer(shape)}
                disabled={isSubmitted}
                className={`
                  relative
                  w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28
                  flex items-center justify-center
                  rounded-2xl
                  border-4
                  transition-all duration-200 ease-bounce
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-prism-purple/50
                  ${getOptionStyles(isSelected, isSubmitted, isCorrect, isCorrectOption)}
                `}
                style={{ animationDelay: `${index * 60}ms` }}
                aria-label={`Select ${PATTERN_SHAPES[shape].label}`}
                aria-pressed={isSelected}
              >
                {getShapeDisplay(shape, "lg")}

                {/* Selection ring */}
                {isSelected && !isSubmitted && (
                  <div
                    className="
                      absolute inset-0
                      rounded-2xl
                      border-4 border-prism-purple
                      pointer-events-none
                    "
                  />
                )}

                {/* Correct answer sparkle */}
                {isSubmitted && isCorrectOption && (
                  <span className="absolute -top-2 -right-2 text-xl animate-sparkle">
                    ✨
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Get styles for option button based on state
 */
function getOptionStyles(
  isSelected: boolean,
  isSubmitted: boolean,
  isCorrect: boolean,
  isCorrectOption: boolean
): string {
  // After submission
  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        bg-gradient-to-b from-meadow-200 via-meadow-300 to-meadow-400
        border-meadow-600
        shadow-[0_4px_0_0_theme(colors.meadow.700),0_8px_20px_rgba(40,167,69,0.25)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        bg-neutral-200 border-neutral-400
        opacity-50 translate-y-0.5
        shadow-[0_2px_0_0_theme(colors.neutral.400)]
      `;
    }
    return `
      bg-neutral-100 border-neutral-300
      opacity-35
      shadow-none
      cursor-default
    `;
  }

  // Selected but not submitted
  if (isSelected) {
    return `
      bg-gradient-to-b from-prism-purple/20 via-prism-purple/40 to-prism-purple/60
      border-prism-purple
      shadow-[0_4px_0_0_theme(colors.prism.purple.dark),0_8px_20px_rgba(162,155,254,0.3)]
      -translate-y-1.5 scale-[1.03]
    `;
  }

  // Default unselected state
  return `
    bg-white border-neutral-300
    shadow-[0_4px_0_0_theme(colors.neutral.300),0_6px_16px_rgba(0,0,0,0.06)]
    hover:border-prism-purple/70 hover:bg-prism-purple/5
    hover:shadow-[0_6px_0_0_rgba(162,155,254,0.3),0_10px_24px_rgba(162,155,254,0.15)]
    hover:-translate-y-1.5
    active:translate-y-0.5 active:shadow-[0_2px_0_0_theme(colors.neutral.300)]
    cursor-pointer
  `;
}

export default ShapePatternGame;
